import { createPopper } from "@popperjs/core";
import ApplicationController from "./application_component_controller";

import URL from "hb/lib/url";
import { debounce } from "hb/lib/utils";
import { useListen } from "hb/insights/use_listen";
import { useOutlets } from "hb/insights/use_outlets";

import { userTZ } from "hb/lib/date_helpers";
import Temporal, { DAY_INT } from "hb/time/temporal";
import Duration from "hb/time/duration";
import Special from "hb/time/special";
import { DEFAULT_TS_INPUT } from "hb/insights/globals";

export default class extends ApplicationController {
  static values = {
    placement: String,
    defaultTs: String,
    defaultTsParam: String,
    defaultTimezone: String,
    visitParams: Boolean,
    useParamState: { Boolean, default: true },
    querySessionId: String,
  };

  static targets = [
    "startInput",
    "endInput",
    "dropdown",
    "month",
    "year",
    "days",
    "localTzBtn",
    "localTzDisplay",
    "toCurrentMonth",
    "quickDropdown",
    "timezoneDropdown",
    "pinAction",
  ];

  connect() {
    useListen(this);
    useOutlets(this);

    [this.startInputTarget, this.endInputTarget].forEach((t) => {
      t.addEventListener("focus", this.handleInputFocused.bind(this));
      t.addEventListener("keyup", this.handleInputKeyUp.bind(this));
      t.addEventListener("input", this.handleInputChanged.bind(this));
      t.addEventListener("keydown", this.preventSubmit.bind(this));
    });

    document.addEventListener("click", this.handleClickOutside.bind(this));

    if (this.useParamStateValue) {
      this.loadTs(URL.getParam("ts") || this.defaultTsParamValue);
      this.listenWindow("urlchanged", this.urlChanged);
    } else {
      this.loadTs(this.defaultTs());
      this.listenWindow("updateQueryState", this.queryStateUpdated);
    }

    this.active = false;
    this.activeSelection = "start";
    this.view = "absolute";
    this.hoverDay = null;
    this.hoverDebounce = debounce(this.renderHover.bind(this), 10);

    this.calendarMonth = Temporal.now();
    this.renderCalendar();
    this.setupTimezone();

    this.element.classList.remove("loading");

    setTimeout(() => {
      this.updateInputs();
      this.popper = createPopper(this.element, this.dropdownTarget, {
        placement: this.placementValue,
        // The query card and .search ancestor use `overflow: hidden` to
        // keep the vis-component bounded in the flex layout. `fixed` makes
        // the dropdown escape that clipping by positioning relative to
        // the viewport.
        strategy: "fixed",
        modifiers: [
          { name: "offset", options: { offset: [0, 5] } },
          { name: "flip", options: { rootBoundary: "viewport" } },
          { name: "preventOverflow", options: { rootBoundary: "viewport" } },
        ],
      });
    }, 10);
  }

  queryStateUpdated({ ts, timezone, querySessionId }) {
    if (this.querySessionIdValue !== querySessionId) {
      return;
    }

    if (ts !== this.currentTs) {
      this.loadTs(ts);
      this.updateInputs();
      this.renderCalendar();
    }

    if (timezone !== this.currentTimezone) {
      if (timezone === undefined || timezone === null) {
        timezone = userTZ();
      }
      this.timezone = timezone;
      this.localTzDisplayTarget.innerHTML =
        Temporal.timezoneAbbreviation(timezone);
    }
  }

  setupTimezone() {
    const { zone, abbr } = Temporal.localTimezone();
    this.localTzBtnTarget.setAttribute("data-tz", zone);
    this.localTzBtnTarget.innerHTML = abbr;

    let startingZone;

    if (this.hasDefaultTimezoneValue) {
      startingZone = this.defaultTimezoneValue;
    } else {
      startingZone = zone;
    }

    let paramTz;
    if (this.useParamStateValue) {
      // Read from URL params (standalone mode)
      const params = URL.params();
      if (params.has("timezone")) {
        paramTz = this.timezone = params.get("timezone");
      } else {
        paramTz = this.timezone = startingZone;
      }
    } else {
      // Use starting zone (dashboard mode)
      paramTz = this.timezone = startingZone;
    }

    if (paramTz !== null) {
      this.localTzDisplayTarget.innerHTML =
        Temporal.timezoneAbbreviation(paramTz);
    }
  }

  updateUrlParam(key, value) {
    if (!this.useParamStateValue) {
      return; // Don't update URL params in dashboard context
    }

    if (this.visitParamsValue) {
      return URL.visitParam(key, value);
    } else {
      return URL.updateParam(key, value);
    }
  }

  timezoneSelect({ target }) {
    let timezone = "UTC";
    if (target.hasAttribute("data-tz")) {
      timezone = target.getAttribute("data-tz");
    } else {
      timezone = target.value;
    }

    let defaultZone;
    if (this.hasDefaultTimezoneValue) {
      defaultZone = this.defaultTimezoneValue;
    } else {
      defaultZone = Temporal.localTimezone().zone;
    }

    this.timezone = timezone; // Store timezone in component state
    let newTimezone = null;

    if (timezone !== defaultZone) {
      newTimezone = timezone;
    }

    this.emitWindow("queryTimezoneChanged", {
      querySessionId: this.querySessionIdValue,
      timezone: newTimezone,
    });
    this.updateUrlParam("timezone", newTimezone);

    this.localTzDisplayTarget.innerHTML =
      Temporal.timezoneAbbreviation(timezone);
    this.timezoneDropdownTarget.classList.remove("show");
  }

  defaultTs() {
    return this.hasDefaultTsValue ? this.defaultTsValue : DEFAULT_TS_INPUT;
  }

  disconnect() {
    [this.startInputTarget, this.endInputTarget].forEach((t) => {
      t.removeEventListener("focus", this.handleInputFocused.bind(this));
      t.removeEventListener("keyup", this.handleInputKeyUp.bind(this));
      t.removeEventListener("input", this.handleInputChanged.bind(this));
      t.removeEventListener("keydown", this.preventSubmit.bind(this));
    });
    document.removeEventListener("click", this.handleClickOutside.bind(this));
  }

  urlChanged() {
    this.loadTs(URL.getParam("ts"));
    this.renderCalendar();
    this.updateInputs();
  }

  loadTs(ts) {
    const [start, end] = ts?.split("/") || [];

    this.start = {
      view: "relative",
      current:
        this.parseTime("fromParam", start) ||
        Duration.fromInput(this.defaultTs()),
    };

    this.end = {
      view: "relative",
      current: this.parseTime("fromParam", end),
    };
  }

  toCurrentMonth() {
    this.calendarMonth =
      this[this.activeSelection].current?.dup() || Temporal.now();
    this.renderCalendar();
    this.checkCurrentMonth();
  }

  checkCurrentMonth() {
    const curr = this[this.activeSelection].current || Temporal.now();

    if (this.view === "absolute" && this.calendarMonth.month !== curr.month) {
      this.toCurrentMonthTarget.classList.remove("hidden");
    } else {
      this.toCurrentMonthTarget.classList.add("hidden");
    }
  }

  renderCalendar() {
    this.yearTarget.innerHTML = this.calendarMonth.yearName;
    this.monthTarget.innerHTML = this.calendarMonth.monthName;

    let { days, startingDay } = this.calendarMonth.monthMeta;

    startingDay -= 1;
    const rows = Math.round((days + startingDay) / 7) + 1;
    const allDays = rows * 7;

    if (this.daysTarget.hasChildNodes()) {
      this.daysTarget.querySelectorAll(".day").forEach((el) => {
        el.removeEventListener(
          "mouseenter",
          this.handleDayMouseEnter.bind(this),
        );
        el.removeEventListener("mouseout", this.handleDayMouseExit.bind(this));
      });
      this.daysTarget.innerHTML = "";
    }

    const start = this.start.current;
    const end = this.end.current || Temporal.now();

    const todayInt = Temporal.now().clearTime().milliseconds;
    const startInt = start.milliseconds;
    const endInt = end.milliseconds;

    this.allDays = [];

    for (let i = 0; i < allDays; i++) {
      const el = document.createElement("div");
      const date = i - startingDay;

      el.addEventListener("mouseenter", this.handleDayMouseEnter.bind(this));
      el.addEventListener("mouseout", this.handleDayMouseExit.bind(this));

      if (date > 0 && date <= days) {
        const currStartDayInt = this.calendarMonth
          .dup()
          .clearTime()
          .setMonthDay(date).milliseconds;
        const currEndDayInt = currStartDayInt + DAY_INT;

        el.classList.add("day");
        el.setAttribute("day-num", date);
        el.setAttribute("day-seconds", currStartDayInt);

        const inStartRange =
          startInt >= currStartDayInt && startInt < currEndDayInt;
        const inEndRange = endInt > currStartDayInt && endInt <= currEndDayInt;

        if (inStartRange && inEndRange) {
          el.classList.add("in-start-range", "in-end-range");

          let startPerc = ((startInt - currStartDayInt) / DAY_INT) * 100;
          let endPerc =
            Math.abs(1 - (endInt - currStartDayInt) / DAY_INT) * 100;

          // Ensure we have at least 20% width for the range
          const diff = 20 - (100 - (startPerc + endPerc));

          if (diff > 0) {
            startPerc -= diff / 2;
            endPerc -= diff / 2;
          }

          if (startPerc > 50) el.classList.add("invert-range");
          if (endPerc > 50) el.classList.add("invert-range");

          el.style.setProperty("--start-range-perc", `${startPerc}%`);
          el.style.setProperty("--end-range-perc", `${endPerc}%`);
        } else if (inStartRange) {
          el.classList.add("in-start-range");
          const perc = ((startInt - currStartDayInt) / DAY_INT) * 100;
          if (perc > 50) el.classList.add("invert-range");
          el.style.setProperty("--start-range-perc", `${perc}%`);
        } else if (inEndRange) {
          el.classList.add("in-end-range");
          const perc = Math.abs(1 - (endInt - currStartDayInt) / DAY_INT) * 100;
          el.style.setProperty("--end-range-perc", `${perc}%`);
          if (perc > 50) el.classList.add("invert-range");
        }

        if (startInt < currStartDayInt && endInt > currEndDayInt) {
          el.classList.add("in-between");
        }

        if (currStartDayInt > todayInt) {
          el.classList.add("inactive");
        }

        if (currStartDayInt === todayInt) {
          el.classList.add("today");
        }

        const span = document.createElement("span");
        span.innerHTML = date;
        el.appendChild(span);
        this.allDays.push(el);
      } else {
        el.classList.add("empty");
      }

      this.daysTarget.appendChild(el);
    }
  }

  calendarSelect(evt) {
    const { target } = evt;
    if (target.classList.contains("inactive")) return;

    const day = parseInt(target.getAttribute("day-num"), 10);

    const active = this[this.activeSelection];
    active.view = "absolute";

    if (this.activeSelection === "start") {
      this.updateActiveSelection("end");
      const currentDay = this.calendarMonth.dup().setMonthDay(day).clearTime();
      active.current = new Temporal(currentDay.date, currentDay.timezone);
      if (
        currentDay.milliseconds >
        (this.end.current?.milliseconds || Temporal.now().milliseconds)
      ) {
        this.end.current = new Temporal(
          this.calendarMonth
            .dup()
            .setMonthDay(day + 1)
            .clearTime().date,
          currentDay.timezone,
        );
      }
    } else {
      this.updateActiveSelection("start");
      const currentDay = this.calendarMonth
        .dup()
        .setMonthDay(day + 1)
        .clearTime();
      active.current = new Temporal(currentDay.date, currentDay.timezone);
      if (currentDay.milliseconds < this.start.current?.milliseconds) {
        this.updateActiveSelection("end");
        this.start.current = new Temporal(
          this.calendarMonth.dup().setMonthDay(day).clearTime().date,
          currentDay.timezone,
        );
      }
    }

    this.updateInputs();
    this.updateParams();
    this.renderCalendar();
    this.checkCurrentMonth();

    // This keeps the handleClickOutside handler from being called.
    evt.preventDefault();
    evt.stopPropagation();
  }

  getTextWidth(text) {
    const offScreenElement = document.createElement("div");
    offScreenElement.style.position = "absolute";
    offScreenElement.style.visibility = "hidden";
    offScreenElement.innerText = text;

    document.body.appendChild(offScreenElement);
    const width = offScreenElement.offsetWidth;
    document.body.removeChild(offScreenElement);

    return width;
  }

  parseTime(fn, str) {
    if (str === "" || str === null) return null;

    let validDate;

    [Duration, Temporal, Special].find((t) => {
      const d = t[fn](str);
      if (d.valid()) {
        validDate = d;
        return true;
      }
    });

    return validDate;
  }

  submitInput() {
    ["start", "end"].forEach((t) => {
      const input = this[`${t}InputTarget`];
      const validDate = this.parseTime("fromInput", input.value);
      if (validDate !== undefined) {
        if (input.value === "" && t === "start") {
          this[t].current = Duration.fromInput(this.defaultTs());
        } else if (input.value === "" && t === "end") {
          this[t].current = null;
        } else {
          this[t].current = validDate;
        }
      }
    });

    this.updateParams();
    this.updateInputs();
    this.renderCalendar();
    this.checkCurrentMonth();
  }

  handleInputKeyUp({ key }) {
    if (key === "Enter") {
      this.submitInput();
    }
  }

  handleDayMouseEnter({ target }) {
    const day = target.getAttribute("day-seconds");
    if (day === null || day === "") return;
    this.hoverDebounce(parseInt(day, 10));
  }

  handleDayMouseExit({ target }) {
    const day = target.getAttribute("day-seconds");
    if (day === null || day === "") return;
    this.hoverDebounce(null);
  }

  renderHover(hoverDayStart) {
    this.allDays.forEach((e) => {
      e.classList.remove(
        "hover-outside-end",
        "hover-outside-start",
        "hover-inside-between",
        "hover-between",
        "hover-selected-start",
        "hover-selected-end",
      );
    });

    const hoverDayEnd = hoverDayStart + DAY_INT;
    const currentStart = this.start.current?.milliseconds;
    const currentEnd = (this.end.current || Temporal.now()).milliseconds;

    if (hoverDayStart !== null) {
      const hoverEl = this.element.querySelector(
        `[day-seconds="${hoverDayStart}"]`,
      );
      if (hoverEl.classList.contains("inactive")) return;

      if (this.activeSelection === "start" && hoverDayStart >= currentEnd) {
        hoverEl.classList.add("hover-outside-start");
      } else if (
        this.activeSelection === "end" &&
        hoverDayEnd <= currentStart
      ) {
        hoverEl.classList.add("hover-outside-end");
      } else {
        this.allDays.forEach((day) => {
          const dayStart = parseInt(day.getAttribute("day-seconds"), 10);
          const dayEnd = dayStart + DAY_INT;

          if (this.activeSelection === "start") {
            if (hoverDayStart == dayStart) {
              day.classList.add("hover-selected-start");
            } else if (dayStart >= hoverDayStart && dayStart < currentStart) {
              day.classList.add("hover-between");
            } else if (dayEnd >= currentStart && dayStart < hoverDayStart) {
              day.classList.add("hover-inside-between");
            }
          } else {
            if (hoverDayStart == dayStart) {
              day.classList.add("hover-selected-end");
            } else if (dayEnd <= hoverDayEnd && dayEnd > currentEnd) {
              day.classList.add("hover-between");
            } else if (dayStart <= currentEnd && dayEnd >= hoverDayEnd) {
              day.classList.add("hover-inside-between");
            }
          }
        });
      }
    }
  }

  preventSubmit(event) {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  }

  handleInputChanged(evt) {
    evt.preventDefault();
    const {
      target: { value, style, dataset },
    } = evt;
    const textWidth = this.getTextWidth(value);
    style.width = textWidth + 30 + "px";
    this[`${dataset.inputRange}TextWidth`] = textWidth;
  }

  handleInputFocused({ target }) {
    this.element.classList.add("active");
    this.updateActiveSelection(target.dataset.inputRange);
    this.checkCurrentMonth();
    this.showDropdown();
  }

  hideDropdown() {
    this.dropdownTarget.classList.remove("visible");
    this.element.classList.remove("active");
    window.removeEventListener("keyup", this.handleDropdownKeyUp.bind(this));
  }

  showDropdown() {
    this.dropdownTarget.classList.add("visible");
    // Recompute the popper position — the reference element may have
    // moved since createPopper ran (panel resize, fullwidth toggle, etc.).
    this.popper?.update();
    window.addEventListener("keyup", this.handleDropdownKeyUp.bind(this));
  }

  handleDropdownKeyUp({ key }) {
    if (key === "Escape") {
      this.element.classList.remove("active");
      this.active = false;
      this.updateInputs();
      this.hideDropdown();
    }
  }

  toggleAbsolute() {
    this.view = this.view === "absolute" ? "relative" : "absolute";

    if (this.view === "absolute") {
      this.element.classList.remove("relative");
      this.element.classList.add("absolute");
    } else {
      this.element.classList.remove("absolute");
      this.element.classList.add("relative");
    }

    this.checkCurrentMonth();
  }

  updateActiveSelection(name) {
    if (name === null) {
      this.active = false;
      this.element.classList.remove("start", "end");
    } else {
      this.activeSelection = name;
      this.element.classList.remove(name == "start" ? "end" : "start");
      this.element.classList.add(name);
    }
  }

  nextMonth() {
    this.calendarMonth = this.calendarMonth.dup().add(1, "month");
    this.renderCalendar();
    this.checkCurrentMonth();
  }

  prevMonth() {
    this.calendarMonth = this.calendarMonth.dup().subtract(1, "month");
    this.renderCalendar();
    this.checkCurrentMonth();
  }

  updateInputs() {
    ["start", "end"].forEach((dir) => {
      const times = this[dir];
      const target = this[`${dir}InputTarget`];

      if (times.current) {
        target.value = times.current.output;
      } else {
        target.value = "";
      }

      target.dispatchEvent(new Event("input", { bubbles: true }));
    });

    this.checkOverflow();
    this.updatePinAction();
  }

  checkOverflow() {
    const totalTextWidth =
      (this.startTextWidth || 0) + (this.endTextWidth || 0);
    const quickWidth = this.element.querySelector(".quick-section").offsetWidth;
    const tzWidth = this.element.querySelector(".timezone-section").offsetWidth;
    const availableWidth = this.element.clientWidth - quickWidth - tzWidth;

    if (totalTextWidth > availableWidth) {
      this.element.classList.add("collapsed");
    } else {
      this.element.classList.remove("collapsed");
    }
  }

  updatePinAction() {
    const hasRelative =
      !(this.start.current instanceof Temporal) ||
      !(this.end.current instanceof Temporal);

    if (hasRelative) {
      this.pinActionTarget.classList.remove("hidden");
    } else {
      this.pinActionTarget.classList.add("hidden");
    }
  }

  convertToAbsolute() {
    this.start.current = Temporal.fromRelative(
      this.start.current,
      this.timezone,
    );
    this.start.view = "absolute";

    this.end.current = Temporal.fromRelative(this.end.current, this.timezone);
    this.end.view = "absolute";

    this.quickDropdownTarget.classList.remove("show");

    this.updateParams();
    this.updateInputs();
    this.renderCalendar();
  }

  setNowRelative({ params: { time } }) {
    this.start.view = "relative";
    if (Special.isSpecial(time)) {
      this.start.current = new Special(time);
    } else {
      this.start.current = Duration.fromInput(time);
    }
    this.end = {};
    this.end.view = "relative";

    this.quickDropdownTarget.classList.remove("show");

    this.updateParams();
    this.updateInputs();
    this.renderCalendar();
  }

  setRelative({ params: { time } }) {
    this.updateRelative(time);
  }

  handleClickOutside({ target }) {
    const inside = [
      this.startInputTarget,
      this.endInputTarget,
      this.dropdownTarget,
    ].find((t) => {
      return t.contains(target);
    });

    if (inside) return;

    this.element.classList.remove("active");
    this.active = false;
    this.updateInputs();
    this.hideDropdown();
  }

  updateParams() {
    let start_r = this.start.current?.param;
    let end_r = this.end.current?.param;
    const ts = [start_r, end_r].filter((n) => n).join("/");

    this.updateUrlParam("ts", ts);
    this.emitWindow("queryTsChanged", {
      ts,
      querySessionId: this.querySessionIdValue,
    });
  }

  updateRelative(time) {
    if (time === "") {
      this[this.activeSelection].current = null;
    } else if (Special.isSpecial(time)) {
      this[this.activeSelection].current = new Special(time);
    } else {
      this[this.activeSelection].current = Duration.fromInput(time);
    }

    this[this.activeSelection].view = "relative";

    this.updateParams();
    this.renderCalendar();
    this.updateInputs();
  }

  // Getters for QueryComponent to access current state
  get currentTs() {
    let start_r = this.start?.current?.param;
    let end_r = this.end?.current?.param;
    return [start_r, end_r].filter((n) => n).join("/");
  }

  get currentTimezone() {
    return this.timezone;
  }
}
